"use client";

import { useEffect, useRef } from "react";
import type { AuthChangeEvent } from "@supabase/supabase-js";
import type { User as DbUser } from "@otalytics/shared";
import { posthog } from "@/lib/analytics";

export function useAuthAnalytics(
  dbUser: DbUser | null,
  authEvent?: AuthChangeEvent | null,
): void {
  const lastIdentifiedId = useRef<string | null>(null);

  useEffect(() => {
    if (!posthog || !dbUser) {
      return;
    }

    const fingerprint = `${dbUser.id}:${dbUser.plan}:${dbUser.isAdmin}:${String(dbUser.createdAt)}`;
    if (lastIdentifiedId.current === fingerprint) {
      return;
    }

    // Identify by user id ONLY as the distinct id (the funnel and the
    // server-side events in #266 join on this). Deliberately do NOT send the
    // email address: it adds no analytics value (we can join back to our own
    // DB on the id) while turning the PostHog profile into directly-identifying
    // PII held by a third party, which weakens the legitimate-interest balance.
    // subscription_tier and account_created_at are coarse, non-identifying
    // account attributes with genuine segmentation value, so they stay.
    // is_internal lets insights exclude admin sessions.
    const isInternal = dbUser.isAdmin === true;
    posthog.identify(dbUser.id, {
      subscription_tier: dbUser.plan,
      account_created_at: dbUser.createdAt,
      is_internal: isInternal,
      role: isInternal ? "admin" : "customer",
    });
    posthog.register({
      is_internal: isInternal,
      role: isInternal ? "admin" : "customer",
    });

    lastIdentifiedId.current = fingerprint;
  }, [dbUser]);

  useEffect(() => {
    if (!posthog) {
      return;
    }

    if (authEvent !== "SIGNED_OUT") {
      return;
    }

    posthog.capture("user_logged_out");
    posthog.reset();
    lastIdentifiedId.current = null;
  }, [authEvent]);
}
